import { groupBy, orderBy, sumBy } from 'lodash'

export interface SafetyViolation {
  teamId: string
  dotNumber: string
  violationDate: Date | null
  reportNumber: string
  violationCode: string
  basic: string
  violationDescription: string
  isOos: boolean
  vin: string
  violationTotalSeverityWeight: number
  dateUpdated: Date | null
  isNew: boolean
}

enum SafetyOverviewChange {
  DECREASE = 'decrease',
  INCREASE = 'increase',
  NO_PREVIOUS_DATA = 'no_previous_data',
}

export interface SafetyOverview {
  dateEnd: Date | null
  dotNumber: string
  teamId: string
  hosComplianceMeasure: number
  hosComplianceMeasureChangeMom: SafetyOverviewChange
  vehicleMaintMeasure: number
  vehicleMaintMeasureChangeMom: SafetyOverviewChange
  driverFitnessMeasure: number
  driverFitnessMeasureChangeMom: SafetyOverviewChange
  drugsAlcoholMeasure: number
  drugsAlcoholMeasureChangeMom: SafetyOverviewChange
  unsafeDrivingMeasure: number
  unsafeDrivingMeasureChangeMom: SafetyOverviewChange
  ratingDateUpdated: Date | null
  driverOss: number
  driverOssChangeMom: SafetyOverviewChange
  driverNationalAverage: number
  hazmatOss: number
  hazmatOssChangeMom: SafetyOverviewChange
  hazmatNationalAverage: number
  vehicleOss: number
  vehicleOssChangeMom: SafetyOverviewChange
  vehicleNationalAverage: number
  dateUpdated: Date | null
}

export function parseSafetyViolation(data: any): SafetyViolation {
  return {
    teamId: data.team_id,
    dotNumber: data.dot_number,
    violationDate: data.violation_date ? new Date(data.violation_date) : null,
    reportNumber: data.report_number,
    violationCode: data.violation_code,
    basic: data.basic,
    violationDescription: data.violation_description,
    isOos: data.is_oos,
    vin: data.vin,
    violationTotalSeverityWeight: data.violation_total_severity_weight,
    dateUpdated: data.date_updated ? new Date(data.date_updated) : null,
    isNew: data.is_new,
  }
}

export function parseSafetyOverview(data: any): SafetyOverview {
  return {
    dateEnd: data.date_end ? new Date(data.date_end) : null,
    dotNumber: data.dot_number,
    teamId: data.team_id,
    hosComplianceMeasure: data.hos_compliance_measure,
    hosComplianceMeasureChangeMom: data.hos_compliance_measure_change_mom,
    vehicleMaintMeasure: data.vehicle_maint_measure,
    vehicleMaintMeasureChangeMom: data.vehicle_maint_measure_change_mom,
    driverFitnessMeasure: data.driver_fitness_measure,
    driverFitnessMeasureChangeMom: data.driver_fitness_measure_change_mom,
    drugsAlcoholMeasure: data.drugs_alcohol_measure,
    drugsAlcoholMeasureChangeMom: data.drugs_alcohol_measure_change_mom,
    unsafeDrivingMeasure: data.unsafe_driving_measure,
    unsafeDrivingMeasureChangeMom: data.unsafe_driving_measure_change_mom,
    ratingDateUpdated: data.rating_date_updated ? new Date(data.rating_date_updated) : null,
    driverOss: data.driver_oss || 0,
    driverOssChangeMom: data.driver_oss_change_mom,
    driverNationalAverage: data.driver_national_average,
    hazmatOss: data.hazmat_oss || 0,
    hazmatOssChangeMom: data.hazmat_oss_change_mom,
    hazmatNationalAverage: data.hazmat_national_average,
    vehicleOss: data.vehicle_oss || 0,
    vehicleOssChangeMom: data.vehicle_oss_change_mom,
    vehicleNationalAverage: data.vehicle_national_average,
    dateUpdated: data.date_updated ? new Date(data.date_updated) : null,
  }
}

export interface SafetyDashboard {
  violationsDetails: SafetyViolation[] | null
  carrierOverview: SafetyOverview | null
}

export interface GroupedSafetyViolation {
  violations: SafetyViolation[]
  basic: string
  basicName: string
  totalWeight: number
  hasNewViolations: boolean
}

export function groupSafetyViolations(violations: SafetyViolation[]) {
  const groups = groupBy(violations, 'basic')
  const groupsFormatted: GroupedSafetyViolation[] = []

  function formatGroup(basic: string, violations: SafetyViolation[]) {
    return {
      violations: violations,
      basic: basic,
      basicName: getSafetyViolationBasicName(basic),
      hasNewViolations: violations.some((v) => v.isNew),
      totalWeight: sumBy(violations, 'violationTotalSeverityWeight'),
    }
  }

  for (const basic in groups) {
    const violationsInGroup = groups[basic]
    groupsFormatted.push(formatGroup(basic, violationsInGroup))
  }

  // Add empty basic categories
  for (const basic of BASIC) {
    if (!groupsFormatted.some((g) => g.basic === basic)) {
      groupsFormatted.push(formatGroup(basic, []))
    }
  }

  return orderBy(groupsFormatted, 'totalWeight', ['desc'])
}

const BASIC = ['Vehicle Maint.', 'HOS Compliance', 'Unsafe Driving', 'Driver Fitness', 'Drugs/Alcohol']

export function getSafetyViolationBasicName(basic: string) {
  if (basic === 'Driver Fitness') return 'Driver fitness'
  if (basic === 'Drugs/Alcohol') return 'Drugs and alcohol'
  if (basic === 'HOS Compliance') return 'HOS compliance'
  if (basic === 'HM Compliance') return 'Hazmat compliance'
  if (basic === 'Unsafe Driving') return 'Unsafe driving'
  if (basic === 'Vehicle Maint.') return 'Vehicle maintenance'
  return basic
}
